import { useMemo, useState } from 'react';
import { useDebounce } from '@odo/shared-ui';
import { FIRST_PAGE } from './browsePaging';
import { DEBOUNCE_MS } from './browserStyles';

export interface BrowseFilters<F extends { search: string }> {
    filters: F;
    // The filters to query with: search lags the box by the debounce.
    effective: F;
    isTyping: boolean;
    updateFilters: (patch: Partial<F>) => void;
    clearFilters: () => void;
}

export function useBrowseFilters<F extends { search: string }>(defaults: F): BrowseFilters<F> {
    const [filters, setFilters] = useState<F>(defaults);
    const debouncedSearch = useDebounce(filters.search, DEBOUNCE_MS);
    const effective = useMemo(
        () => ({ ...filters, search: debouncedSearch }),
        [filters, debouncedSearch]
    );
    return {
        filters,
        effective,
        isTyping: filters.search !== debouncedSearch,
        updateFilters: (patch) => setFilters((current) => ({ ...current, ...patch })),
        clearFilters: () => setFilters(defaults)
    };
}

// A new scope key (filters, sort or mode changed) lands back on page 1 without an extra fetch.
export function useScopedPage(scopeKey: string): [number, (page: number) => void] {
    const [pageState, setPageState] = useState({ scopeKey, page: FIRST_PAGE });
    const page = pageState.scopeKey === scopeKey ? pageState.page : FIRST_PAGE;
    return [page, (next) => setPageState({ scopeKey, page: next })];
}
